/**
 * Break glass — tijdelijke, gelogde toegang van een beheerder tot de gegevens
 * van één leerling.
 *
 * WAAROM DIT BESTAAT
 *
 * Migratie 0008 bouwt een auditspoor op: een beheerder mag de mastery,
 * toetsuitslagen en rapporten van een individueel kind pas zien na een
 * expliciet toegangsvenster met een opgegeven reden, en dat venster wordt
 * vastgelegd. `has_active_data_access()` bewaakt dat aan de databasekant.
 *
 * De adminschermen liepen daar volledig omheen: ze lazen alles rechtstreeks
 * uit de lokale store, dus het mechanisme bestond wel maar deed niets. Deze
 * module is de clientkant die er hoort te zijn.
 *
 * DE GRENS
 *
 * Dit is GEEN beveiligingsgrens — dat is RLS. Zodra de dataLayer op de server
 * draait weigert de database het antwoord gewoon zonder actieve grant. Wat
 * deze module oplost is dat de beheerder de gegevens nu al niet ONGEVRAAGD
 * te zien krijgt, en dat elke inzage een spoor achterlaat.
 *
 * Daarom is de faalstand ook dicht: kan de status niet worden opgehaald, dan
 * geldt "geen toegang". Bij twijfel niets tonen kost hooguit een extra klik;
 * bij twijfel wél tonen is een inzage zonder grondslag.
 */

import { supabase } from './client';
import { error as logError } from '../../utils/logger';
export interface AccessGrant {
  /** Id van de grant, nodig om hem vroegtijdig in te trekken. */
  id: string;
  learnerId: string;
  /** Wanneer het venster vanzelf sluit. */
  expiresAt: number;
}
export interface RequestResult {
  ok: boolean;
  grant?: AccessGrant;
  message?: string;
}

/**
 * Opent een toegangsvenster voor één leerling.
 *
 * De reden is verplicht en gaat mee naar het auditlog. Dat is de kern van het
 * mechanisme: niet "wie kon erbij" maar "wie was erbij, en waarom".
 */
/**
 * Hoe lang een break-glass-venster openstaat.
 *
 * Eén constante, omdat dit getal een BELOFTE is: het scherm zegt "30 min" tegen
 * de beheerder, en het auditlog legt datzelfde venster vast. Stonden die twee los
 * van elkaar, dan kan de knop iets anders beweren dan er gebeurt.
 *
 * LET OP — de server-RPC `request_data_access` (migratie 0008) heeft zijn eigen
 * default van 60 minuten. Dat verschil is nu onschadelijk omdat deze client de
 * waarde altijd expliciet meestuurt, maar een andere aanroeper (de SQL-console,
 * een toekomstige tool) die de parameter weglaat, krijgt stil een heel uur. Dat
 * staat als openstaand punt in het SSOT-register van het adminportaal.
 */
export const BREAK_GLASS_MINUTES = 30;
export async function requestDataAccess(learnerId: string, reason: string, minutes = BREAK_GLASS_MINUTES): Promise<RequestResult> {
  const trimmed = reason.trim();
  if (trimmed.length < 10) {
    return {
      ok: false,
      message: 'Geef een reden van minstens 10 tekens op. Deze wordt vastgelegd in het auditlog.'
    };
  }
  const {
    data,
    error
  } = await supabase.rpc('request_data_access', {
    p_learner_id: learnerId,
    p_reason: trimmed,
    p_minutes: minutes
  });
  if (error || !data) {
    logError('[BreakGlass] Toegang aanvragen mislukt', error);
    return {
      ok: false,
      message: 'Het openen van een toegangsvenster is niet gelukt. Er is geen toegang verleend.'
    };
  }

  // `request_data_access` geeft ALLEEN het id van de grant terug (een uuid),
  // geen rij met `expires_at`. Dat is makkelijk mis te lezen, en de fout is
  // stil: leest de client `data.expires_at`, dan is de vervaltijd `NaN`, en
  // dan is elke vergelijking `expiresAt > Date.now()` onwaar. Het venster
  // gaat dan open op de server en blijft dicht in de UI — een beheerder die
  // een reden intikt en niets ziet gebeuren, terwijl er wél een auditregel
  // wordt weggeschreven. De vervaltijd wordt daarom hier afgeleid uit de
  // duur die we zelf meesturen.
  const grantId = String(data);

  // Bevestigen bij de server dat het venster echt geldig is, in plaats van
  // aannemen dat een geslaagde insert ook toegang betekent. Weigert de
  // database het alsnog (bijvoorbeeld omdat de rol geen admin is), dan komen
  // we daar nú achter en niet pas bij een lege lijst verderop.
  const active = await hasActiveDataAccess(learnerId);
  if (!active) {
    return {
      ok: false,
      message: 'De database heeft de toegang niet bevestigd. Controleer of dit account de beheerdersrol heeft.'
    };
  }
  return {
    ok: true,
    grant: {
      id: grantId,
      learnerId,
      expiresAt: Date.now() + minutes * 60_000
    }
  };
}

/**
 * Sluit een venster voordat het vanzelf afloopt.
 *
 * Bewust een eigen knop en niet alleen een timer: een beheerder die klaar is
 * hoort te kunnen afsluiten. Een venster dat blijft openstaan tot het
 * verloopt, is toegang die langer duurt dan nodig.
 */
export async function revokeDataAccess(grantId: string): Promise<boolean> {
  const {
    error
  } = await supabase.rpc('revoke_data_access', {
    p_grant_id: grantId
  });
  if (error) {
    logError('[BreakGlass] Intrekken mislukt', error);
    return false;
  }
  return true;
}

/** Eén regel uit het break-glass-logboek. */
export interface AccessLogEntry {
  id: string;
  /** Naam van de beheerder die het venster opende. */
  adminLabel: string;
  /** Leerling-id, of null bij een systeembreed venster. Nooit een naam. */
  learnerId: string | null;
  reason: string;
  grantedAt: number;
  expiresAt: number;
  revokedAt: number | null;
  /** Staat dit venster nu nog open? */
  isActive: boolean;
}

/**
 * Leest het break-glass-logboek (migratie 0008, uitleesbaar sinds 0033).
 *
 * Elke beheerder ziet ALLE grants en niet alleen zijn eigen: break glass steunt
 * op onderling toezicht, en een logboek waarin je alleen je eigen handelen ziet,
 * controleert niemand.
 *
 * De leerling komt als id terug en wordt clientzijdig gelabeld met
 * `pseudonymFor`, zodat het logboek niet zelf de naam prijsgeeft van het kind
 * wiens gegevens zijn ingezien. Anders is het lezen van het logboek een nieuwe
 * inzage.
 */
export async function listDataAccessGrants(limit = 100): Promise<{
  ok: boolean;
  entries: AccessLogEntry[];
  message?: string;
}> {
  const {
    data,
    error
  } = await supabase.rpc('admin_list_data_access', {
    p_limit: limit
  });
  if (error) {
    logError('[BreakGlass] Logboek ophalen mislukt', error);
    return {
      ok: false,
      entries: [],
      message: 'Het inzage-logboek kon niet worden opgehaald. Draai migratie 0033 als deze melding blijft staan.'
    };
  }
  const rows = (data ?? []) as Array<{
    id: string;
    admin_label: string | null;
    learner_id: string | null;
    reason: string;
    granted_at: string;
    expires_at: string;
    revoked_at: string | null;
    is_active: boolean;
  }>;
  return {
    ok: true,
    entries: rows.map(r => ({
      id: r.id,
      adminLabel: r.admin_label ?? 'Beheerder',
      learnerId: r.learner_id,
      reason: r.reason,
      grantedAt: new Date(r.granted_at).getTime(),
      expiresAt: new Date(r.expires_at).getTime(),
      revokedAt: r.revoked_at ? new Date(r.revoked_at).getTime() : null,
      isActive: r.is_active
    }))
  };
}

/**
 * Heeft de huidige beheerder nu toegang tot deze leerling?
 *
 * Faalt dicht: elke fout levert `false`. Zie de kop van dit bestand.
 */
export async function hasActiveDataAccess(learnerId: string): Promise<boolean> {
  const {
    data,
    error
  } = await supabase.rpc('has_active_data_access', {
    p_learner_id: learnerId
  });
  if (error) {
    logError('[BreakGlass] Status ophalen mislukt', error);
    return false;
  }
  return data === true;
}